<script setup>
// ÁUDIO ENVIADO PELO ESCRITÓRIO (telefonia, etapa E — 25/09/2026): o da URA e
// a mensagem de entrada da fila. Tocador quando existe, área de envio quando
// não; confere tipo e tamanho antes (o servidor confere de novo). Devolve o
// arquivo escolhido e se o atual deve sair.
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  atual: { type: Object, default: null },
  rotulo: { type: String, required: true },
  dica: { type: String, default: '' },
});
const arquivo = defineModel('arquivo', { type: Object, default: null });
const tirarAudio = defineModel('tirar', { type: Boolean, default: false });

const { t } = useI18n();
const T = 'TELEFONIA.ROTEAMENTO.URA';
const previa = ref('');
const erroDoAudio = ref('');
const seletor = ref(null);
const MAX = 10 * 1024 * 1024;

function escolher(e) {
  const f = e.target.files?.[0];
  e.target.value = '';
  if (!f) return;
  erroDoAudio.value = '';
  if (!f.type.startsWith('audio/')) {
    erroDoAudio.value = t(`${T}.AUDIO_TIPO`);
    return;
  }
  if (f.size > MAX) {
    erroDoAudio.value = t(`${T}.AUDIO_GRANDE`);
    return;
  }
  if (previa.value) URL.revokeObjectURL(previa.value);
  arquivo.value = f;
  previa.value = URL.createObjectURL(f);
  tirarAudio.value = false;
}
function remover() {
  if (previa.value) URL.revokeObjectURL(previa.value);
  arquivo.value = null;
  previa.value = '';
  tirarAudio.value = Boolean(props.atual);
}
onBeforeUnmount(() => previa.value && URL.revokeObjectURL(previa.value));

const audioAtual = computed(() => {
  if (arquivo.value) return { nome: arquivo.value.name, url: previa.value };
  if (tirarAudio.value || !props.atual) return null;
  return props.atual;
});
</script>

<template>
  <div class="aud">
    <span class="advup-rotulo">{{ rotulo }}</span>
    <div v-if="audioAtual" class="aud__audio">
      <span class="aud__onda" aria-hidden="true">
        <i class="i-ph-waveform-bold" />
      </span>
      <div class="aud__audio-info">
        <p class="aud__audio-nome">{{ audioAtual.nome }}</p>
        <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
        <audio
          :src="audioAtual.url"
          controls
          preload="none"
          class="aud__player"
        />
      </div>
      <div class="aud__audio-acoes">
        <button
          type="button"
          class="advup-acao"
          :aria-label="t(`${T}.TROCAR_AUDIO`)"
          @click="seletor?.click()"
        >
          <i class="i-hugeicons-reload" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="advup-acao advup-acao--perigo"
          :aria-label="t(`${T}.TIRAR_AUDIO`)"
          @click="remover"
        >
          <i class="i-hugeicons-delete-02" aria-hidden="true" />
        </button>
      </div>
    </div>
    <button v-else type="button" class="aud__enviar" @click="seletor?.click()">
      <i class="i-ph-upload-simple-bold" aria-hidden="true" />
      <span class="aud__enviar-tit">{{ t(`${T}.ENVIAR_AUDIO`) }}</span>
      <span class="advup-nota">{{ dica }}</span>
    </button>
    <input
      ref="seletor"
      type="file"
      accept="audio/*"
      class="hidden"
      :aria-label="t(`${T}.ENVIAR_AUDIO`)"
      @change="escolher"
    />
    <span v-if="erroDoAudio" class="advup-nota advup-nota--erro">{{
      erroDoAudio
    }}</span>
  </div>
</template>

<style scoped>
.aud {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  min-width: 0;
}
.aud__audio {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-3);
  border: 1px solid rgb(var(--teal-6));
  border-radius: var(--r-4);
  background: radial-gradient(
      circle at 0 50%,
      rgb(var(--teal-9) / 12%),
      transparent 60%
    ),
    rgb(var(--solid-1));
}
.aud__onda {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-3);
  font-size: var(--ico-3);
  background: rgb(var(--teal-9));
  color: white;
}
.aud__audio-info {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}
.aud__audio-nome {
  margin: 0;
  overflow: hidden;
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aud__player {
  width: 100%;
  height: 32px;
}
.aud__audio-acoes {
  display: flex;
  gap: var(--esp-1);
}
.aud__enviar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--esp-1);
  padding: var(--esp-5) var(--esp-4);
  border: 1.5px dashed rgb(var(--slate-6));
  border-radius: var(--r-4);
  text-align: center;
  cursor: pointer;
  touch-action: manipulation;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    background var(--mov-1) var(--mov-curva);
}
.aud__enviar:hover {
  border-color: rgb(var(--teal-8));
  background: rgb(var(--teal-2));
}
.aud__enviar > i {
  font-size: var(--ico-4, 28px);
  color: rgb(var(--teal-10));
}
.aud__enviar-tit {
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
}
</style>
